// @ts-nocheck

import { Label } from '@branchjs/neo-components';
import React from 'react';
import Link from '../../../common/components/core/Link';
import DiscoAdsAppIconWrapper from './DiscoAdsComponents/DiscoAdsAppIconWrapper';
import DiscoAdsTableInputWrapper from './DiscoAdsComponents/DiscoAdsTableInputWrapper';
import DiscoAdsTableStatusWrapper from './DiscoAdsComponents/DiscoAdsTableStatusWrapper';

import { hilightedTableRow } from './discoAdsCommonStyles';
import { ACTIVE, ALL, DA_CREATIVE_CHAR_LIMIT, NEW_AD_OBJECT_LABEL_COPY, PAUSED } from './discoAdsConstants';

import {
  castToDecimalPrecisePercentageStr,
  formatStringToDollar,
  sortRows,
  strToTitleCase,
} from '../../../main/support/utils';

// formatStatusColumn handles formatting for all 'status' columns in each Disco Ads table
export const formatStatusColumn = ({ col, type, handleStatusChange, campaignStatus, adGroupStatus }) => {
  return {
    ...col,
    // biome-ignore lint/correctness/noUnusedVariables: ignored using `--suppress`
    formatter: (val, index, data) => {
      const campaignIsPaused = campaignStatus && campaignStatus === PAUSED;
      const adGroupIsPaused = adGroupStatus && adGroupStatus === PAUSED;
      const { id } = data;
      return val && val.length ? (
        <DiscoAdsTableStatusWrapper
          val={val}
          id={id}
          type={type}
          onChange={handleStatusChange}
          campaignIsPaused={campaignIsPaused || null}
          adGroupIsPaused={adGroupIsPaused || null}
        />
      ) : (
        val
      ); // allow smartTable to handle a falsey value
    },
  };
};

export const formatNameColumn = ({ col, changeView, linkedNameSpace, newAdObjectId }) => {
  return {
    ...col,
    // biome-ignore lint/correctness/noUnusedVariables: ignored using `--suppress`
    formatter: (val, index, data) => {
      return (
        <div className="da-name-row__container">
          <Link
            className="da-name-row__text"
            onClick={() => changeView({ view: linkedNameSpace, data })}
            testid="disco-ads-name-key"
          >
            {val}
          </Link>
          {newAdObjectId && newAdObjectId === data?.id && <Label variant="default">{NEW_AD_OBJECT_LABEL_COPY}</Label>}
        </div>
      );
    },
  };
};

export const formatBidColumn = ({ col, onConfirmChange }) => {
  return {
    ...col,
    // biome-ignore lint/correctness/noUnusedVariables: ignored using `--suppress`
    formatter: (val, index, data) => {
      const dollarFormattedVal = `$${formatStringToDollar(val.toFixed(2))}`;
      return (
        <DiscoAdsTableInputWrapper type="bid" input={dollarFormattedVal} id={data.id} onConfirmChange={onConfirmChange} />
      );
    },
  };
};

export const formatContentUrlColumn = ({ col, onConfirmChange }) => {
  return {
    ...col,
    // biome-ignore lint/correctness/noUnusedVariables: ignored using `--suppress`
    formatter: (val, index, data) => {
      const { id } = data;
      return val ? (
        <DiscoAdsTableInputWrapper type="url" input={val} creativeId={id} onConfirmChange={onConfirmChange} />
      ) : (
        val
      ); // allow smartTable to handle a falsey value
    },
  };
};

export const formatStaticTableColumns = (cols) => {
  const formattedColumns = cols.map((col) => {
    const activeColumn = {
      ...col,
      active: true,
    };
    switch (col.key) {
      case 'spend':
      case 'budget_remaining':
        return {
          ...activeColumn,
          formatter: (val) => {
            return val !== undefined ? `$${formatStringToDollar(val.toFixed(2))}` : null;
          },
        };
      case 'average_cpc':
        return {
          ...activeColumn,
          formatter: (val) => {
            return val ? `$${formatStringToDollar(val.toFixed(2))}` : null;
          },
        };
      case 'ctr':
      case 'cti':
        return {
          ...activeColumn,
          formatter: (val) => {
            return val ? castToDecimalPrecisePercentageStr(val, 2) : null;
          },
        };
      case 'icon_url': // for creative app icon
        return {
          ...activeColumn,
          // biome-ignore lint/correctness/noUnusedVariables: ignored using `--suppress`
          formatter: (val, index, data) => {
            const { name } = data;
            return val ? <DiscoAdsAppIconWrapper url={val} title={name} /> : null;
          },
        };
      case 'name': // this 'name' case will only run for creative titles which use the 'name' key
      case 'description':
        return {
          ...activeColumn,
          formatter: (val) => val && <div className="da-200px-min-width">{val}</div>,
        };
      case 'type':
        return {
          ...activeColumn,
          formatter: (val) => (val && val.length ? strToTitleCase(val).replace('_', '-') : null),
        };
      case 'popularity':
        return {
          ...activeColumn,
          formatter: (val) => (val?.length ? strToTitleCase(val) : null),
        };
      default:
        return {
          ...activeColumn,
        };
    }
  });
  return formattedColumns;
};

export const hasDateRangeChanged = ({ currentDateRange, prevDateRange }) => {
  if (currentDateRange) {
    return (
      currentDateRange.start_date !== prevDateRange?.start_date || currentDateRange.end_date !== prevDateRange?.end_date
    );
  }
  return false;
};

const splitActiveAndPausedRows = (rows) => {
  const result = {
    activeRows: [],
    pausedRows: [],
  };
  return rows.reduce((acc, row) => {
    if (row.status === ACTIVE) {
      acc.activeRows.push(row);
    } else {
      acc.pausedRows.push(row);
    }
    return acc;
  }, result);
};

export const sortTableRows = ({ columns, rows, activeHeader, isReversedHeader, filterStatusValue }) => {
  if (filterStatusValue === ALL) {
    const { activeRows, pausedRows } = splitActiveAndPausedRows(rows);
    const activeRowsSorted = sortRows(null, columns, activeHeader, !isReversedHeader, activeRows);
    const pausedRowsSorted = sortRows(null, columns, activeHeader, !isReversedHeader, pausedRows);
    return [...activeRowsSorted, ...pausedRowsSorted];
  }
  return sortRows(null, columns, activeHeader, !isReversedHeader, rows);
};

// isBidValid prevents a user from fat-fingering a bid entry in the UI. This function deems the following bids invalid:
// empty string bid, bids with non-numerical characters, and bids with more than one decimal point
export const isBidValid = (str) => {
  if (!str.length || str === '.') {
    return false;
  }
  if (str !== str.replace(/[^\d.-]/g, '')) {
    return false;
  }
  const charMap = new Map();
  str.split('').forEach((char) => {
    charMap.set(char, (charMap.get(char) || 0) + 1);
  });
  if (charMap.get('.') > 1) {
    return false;
  }
  return true;
};

export const formatStringBidToTwoDecimals = (strBidWithoutDollarSign) => {
  const numBid = Number(strBidWithoutDollarSign);
  const bidToTwoDecimals = Math.floor(numBid * 100) / 100;
  return bidToTwoDecimals;
};

export const highlightNewAdObjectInTable = ({ rows, newAdObjectId }) => {
  // the term ad object can refer to either a campaign or an ad group
  const newAdObjectRowIndex = rows.findIndex((row) => row.id === newAdObjectId);
  const highlightedAdObject = Object.assign({}, rows[newAdObjectRowIndex], {
    style: hilightedTableRow,
  });
  const rowsWithHighlightedAdObjectOnTop = [
    highlightedAdObject,
    ...rows.slice(0, newAdObjectRowIndex),
    ...rows.slice(newAdObjectRowIndex + 1),
  ];
  return rowsWithHighlightedAdObjectOnTop;
};

export const formatGeoEntitiesForSearchAndSelect = (geoEntities) => {
  if (!geoEntities) {
    return [];
  }
  return geoEntities.map((geoEntity) => {
    const { full_pretty_name, type, id, country_code, pretty_name } = geoEntity;
    return {
      label: full_pretty_name,
      value: full_pretty_name,
      countryCode: country_code,
      type,
      id,
      prettyName: pretty_name,
    };
  });
};

export const isCharLimitExceeded = ({ key, strLength }) => {
  return strLength > DA_CREATIVE_CHAR_LIMIT[key];
};
